import { Typography, useTheme } from '@mui/material'
import { Stack } from '@vapor/material'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

export type ReadOnlyFieldProps = {
  label?: React.ReactNode
  value: React.ReactNode
  updatedValue?: React.ReactNode
  error?: string | null
  clickable?: boolean
  onClick?: () => void
  direction?: 'vertical' | 'horizontal'
  variant?: 'compact' | 'summary'
}

export default function ReadOnlyField({ label, value, updatedValue, error, clickable, onClick, direction = 'horizontal', variant = 'compact' }: ReadOnlyFieldProps) {
  const { t } = useTranslation()
  const theme = useTheme()

  const color = useMemo(() => (error ? theme.palette.error.main : undefined), [error])
  const updatedValueColor = useMemo(() => 'var(--IslamicGreen)', [])

  const { labelVariant, fieldVariant } = useMemo(() => {
    let labelVariant = <></>
    let fieldVariant = <></>
    switch (variant) {
      case 'compact':
        labelVariant = (
          <Typography variant="caption" component="span" color={color}>
            <strong>{label}</strong>
          </Typography>
        )
        fieldVariant = (
          <Typography variant="caption" component="span">
            {value}
          </Typography>
        )
        break
      case 'summary':
        labelVariant = (
          <Typography variant="caption" component="span" color={color} sx={{ pt: 1 }}>
            {label}
          </Typography>
        )
        fieldVariant = (
          <Typography variant="boldLabel" component="span" color={'var(--DarkImperialBlue)'} sx={{ borderBottom: '1px solid #ccc', pb: 1 }}>
            {value ?? '-'}
          </Typography>
        )
        break
    }

    return { labelVariant, fieldVariant }
  }, [label, value, variant])

  const valueHasAnUpdate: boolean = useMemo(() => {
    if (updatedValue && value !== updatedValue) {
      return true
    }
    return false
  }, [value, updatedValue])

  return (
    <Stack
      direction="column"
      spacing={0}
      onClick={() => {
        if (clickable) {
          onClick?.()
        }
      }}
      style={{ cursor: clickable ? 'pointer' : undefined }}
    >
      <Stack direction={direction === 'horizontal' ? 'row' : 'column'} spacing={1}>
        {labelVariant}
        {fieldVariant}
      </Stack>
      {valueHasAnUpdate && (
        <Typography variant="caption" color={updatedValueColor}>
          {t('commons.new_value')} {updatedValue}
        </Typography>
      )}
      {(error ? true : false) && (
        <Typography variant="caption" color={color}>
          {error}
        </Typography>
      )}
    </Stack>
  )
}
